<template>
  <q-dialog
    v-model="shown"
    :maximized="$q.screen.lt.md"
  >
    <q-card style="min-width: 50%;">
      <q-banner
        v-if="summaryOnly"
        class="bg-warning q-mb-sm sticky-banner"
      >
        <template #avatar>
          <q-icon
            name="warning"
            color="negative"
          />
        </template>
        {{ differentSiteWarning }}
      </q-banner>
      <q-card-section class="row items-center q-mx-xs">
        <div
          class="text-h6"
        >
          Dispute: {{ dispute.id }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
          @click="$emit('closed')"
        />
      </q-card-section>
      <q-card-section>
        <div class="q-table__title">
          Summary
        </div>
        <div
          class="row justify-end q-gutter-sm q-mt-xs"
        />
        <div class="row q-gutter-md q-mb-md">
          <q-input
            v-model="dispute.amount"
            class="col"
            label="Amount"
            :prefix="currentCircuit.localeCurrencySymbol"
            type="number"
            readonly
          />
          <q-input
            v-model="dispute.netAmount"
            class="col"
            label="Net Amount"
            :prefix="currentCircuit.localeCurrencySymbol"
            type="number"
            readonly
          />
          <q-input
            v-model="dispute.fee"
            class="col"
            label="Fee"
            :prefix="currentCircuit.localeCurrencySymbol"
            type="number"
            readonly
          />
          <q-input
            :model-value="dispute.createdAt ? dispute.createdAt.i18n('medium') : null"
            class="col"
            label="Created At"
            readonly
          />
        </div>
        <div class="row q-gutter-md q-mb-md">
          <q-input
            v-if="dispute.status"
            :model-value="$_.startCase(dispute.status)"
            class="col"
            label="Status"
            readonly
          />
          <q-input
            v-if="dispute.reason"
            :model-value="$_.startCase(dispute.reason.replace('_', ' '))"
            class="col"
            label="Reason"
            readonly
          />
          <q-input
            v-model="dispute.evidenceDueBy"
            class="col"
            label="Evidence Due By"
            readonly
          />
        </div>
        <q-inner-loading :showing="dispute.loading || dispute.saving" />
      </q-card-section>
      <q-card-section
        v-if="dispute.evidence && !summaryOnly"
      >
        <div class="q-table__title">
          Evidence
        </div>
        <q-input
          v-model="dispute.evidence.customerName"
          class="col q-mt-sm"
          label="Customer Full Name (Optional)"
          :disable="!allowDispute"
          bottom-slots
        />
        <q-input
          v-model="dispute.evidence.customerEmailAddress"
          class="col q-mt-sm"
          label="Customer Email Address (Optional)"
          :disable="!allowDispute"
          bottom-slots
        />
        <div>
          <q-input
            v-model="dispute.evidence.productDescription"
            class="col q-mt-sm"
            label="Product Description *"
            :disable="!allowDispute"
            type="textarea"
            autogrow
            :rules="[val => !!val || 'Field is required']"
            bottom-slots
          />
          <q-tooltip>A description of the product or service that was sold.</q-tooltip>
        </div>
        <div>
          <q-input
            :model-value="dispute.evidence.serviceDate.i18n('shortDate')"
            class="col q-mt-sm"
            label="Service Date *"
            :disable="!allowDispute"
            :rules="[val => !!val || 'Field is required']"
            bottom-slots
          >
            <template #append>
              <q-icon
                name="event"
                class="cursor-pointer"
              >
                <q-popup-proxy
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-date
                    v-model="dispute.evidence.serviceDate"
                    mask="YYYY-MM-DD"
                  />
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
          <q-tooltip>The date on which the customer received or began receiving the purchased service.</q-tooltip>
        </div>
        <div>
          <q-input
            v-model="dispute.evidence.cancellationPolicyDisclosure"
            class="col q-mt-sm"
            label="Cancellation Policy Disclosure (Optional)"
            :disable="!allowDispute"
            type="textarea"
            autogrow
            bottom-slots
          />
          <q-tooltip>An explanation of how and when the customer was shown your refund policy prior to purchase.</q-tooltip>
        </div>
        <div>
          <q-input
            v-model="dispute.evidence.cancellationRebuttal"
            class="col q-mt-sm"
            label="Cancellation Rebuttal (Optional)"
            :disable="!allowDispute"
            type="textarea"
            autogrow
            bottom-slots
          />
          <q-tooltip>A justification for why the customer’s subscription was not canceled.</q-tooltip>
        </div>
        <div>
          <q-input
            v-model="dispute.evidence.refundPolicyDisclosure"
            class="col q-mt-sm"
            label="Refund Policy Disclosure (Optional)"
            :disable="!allowDispute"
            type="textarea"
            autogrow
            bottom-slots
          />
          <q-tooltip>Documentation demonstrating that the customer was shown your refund policy prior to purchase.</q-tooltip>
        </div>
        <div>
          <q-input
            v-model="dispute.evidence.refundRefusalExplanation"
            class="col q-mt-sm"
            label="Refund Refusal Explanation (Optional)"
            :disable="!allowDispute"
            type="textarea"
            autogrow
            bottom-slots
          />
          <q-tooltip>A justification for why the customer is not entitled to a refund.</q-tooltip>
        </div>
        <div>
          <q-input
            v-model="dispute.evidence.uncategorizedText"
            class="col q-mt-sm"
            label="Uncategorized Text (Optional)"
            :disable="!allowDispute"
            type="textarea"
            autogrow
            bottom-slots
          />
          <q-tooltip>Any other additional relevant information.</q-tooltip>
        </div>
        <div v-if="allowDispute">
          <div class="text-subtitle1 q-mt-sm">
            Attachments  <q-icon name="info">
              <q-tooltip>Optional attachments to provide additional evidence (Not a requirement)</q-tooltip>
            </q-icon>
          </div>
          <div class="row q-ml-sm q-mt-sm">
            <div class="col-4">
              <div>Service Documentation</div>
              <q-tooltip>Documentation showing proof that a service was provided to the customer. This could include a copy of a signed contract, work order, or other form of written agreement.</q-tooltip>
              <file-uploader
                v-model="dispute.evidence.serviceDocumentationBlob"
                :model="dispute.evidence"
                :loading-model="dispute"
                url-property="serviceDocumentationBlob"
              />
            </div>
            <div class="col-4">
              <div>Uncategorized File</div>
              <q-tooltip>Any additional evidence or statements.</q-tooltip>
              <file-uploader
                v-model="dispute.evidence.uncategorizedFileBlob"
                :model="dispute.evidence"
                :loading-model="dispute"
                url-property="uncategorizedFileBlob"
              />
            </div>
            <div class="col-4">
              <div>Customer Communication</div>
              <q-tooltip>Any communication with the customer that you feel is relevant to your case. Examples include emails proving that the customer received the product or service, or demonstrating their use of or satisfaction with the product or service.</q-tooltip>
              <file-uploader
                v-model="dispute.evidence.customerCommunicationBlob"
                :model="dispute.evidence"
                :loading-model="dispute"
                url-property="customerCommunicationBlob"
              />
            </div>
          </div>
        </div>
        <q-inner-loading :showing="dispute.loading || dispute.saving" />
      </q-card-section>
      <q-card-actions
        v-if="!summaryOnly"
        class="row justify-end q-ma-sm"
      >
        <q-btn
          v-if="allowDispute"
          label="Update Evidence"
          color="secondary"
          :loading="dispute.saving"
          :disable="!dispute.evidence.serviceDate || !dispute.evidence.productDescription || dispute.loading"
          @click="save()"
        />
        <q-btn
          v-close-popup
          label="Close"
          color="primary"
          :loading="dispute.saving"
          @click="$emit('closed')"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>

import FileUploader from 'src/modules/admin/components/FileUploader'

export default {
  name: 'DisputeDialog',
  components: {
    FileUploader
  },
  configuration: ['currentCircuit'],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    }
  },
  emits: ['closed', 'update:model-value'],
  data () {
    return {
      shown: false,
      id: null,
      summaryOnly: false,
      siteName: null
    }
  },
  computed: {
    allowDispute () {
      const status = this.dispute.status
      return (status === 'warning_needs_response' || status === 'needs_response') && this.dispute.submissionCount === 0
    },
    differentSiteWarning () {
      return `This dispute was created on a different site (${this.siteName}).  Please change sites to view or manage it.`
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
      if (this.shown) {
        this.$trackPage(this, { orderId: this.id })
      }
    }
  },
  methods: {
    show (id, summaryOnly = false, siteName = null) {
      this.id = id
      this.shown = true
      this.summaryOnly = summaryOnly
      this.siteName = siteName
    },
    async save () {
      if (this.summaryOnly) return

      this.dispute.saving = true
      this.dispute.status = 'under_review'
      this.dispute.hasEvidence = true
      this.dispute.evidence.disputeCode = this.dispute.id
      await this.dispute.evidence.save()
      this.dispute.saving = false
      this.shown = false
      this.$emit('closed')
    }
  },
  models: {
    dispute: {
      variables () {
        return {
          id: this.id
        }
      },
      evidence: true
    }
  }
}

</script>

<style lang="scss" scoped>
</style>
